/**
 * CotizacionesCatalogo - CRUD de cotizaciones
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Plus,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Trash2,
  Search,
  X,
  RefreshCw,
  AlertCircle,
  Eye,
  ArrowRightLeft,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { useSucursales } from "@/hooks/use-sucursales";
import {
  useDeactivateCotizacion,
  useActivateCotizacion,
  useDeleteCotizacion,
  type CotizacionListItem,
} from "@/hooks/use-ordenes";
import { getCotizaciones } from "@/hooks/use-ordenes";

import { CotizacionFormDialog } from "./cotizacion-form-dialog";
import { CotizacionDetailSheet } from "./cotizacion-detail-sheet";

function getEstadoBadgeVariant(
  estado: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (estado) {
    case "VIGENTE":
      return "default";
    case "CONVERTIDA":
      return "secondary";
    case "VENCIDA":
      return "outline";
    case "CANCELADA":
      return "destructive";
    default:
      return "secondary";
  }
}

export function CotizacionesCatalogo() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();
  const { isAllSucursalesSelected, sucursalActiva, canViewAllSucursales } = useSucursal();
  const { data: sucursales = [] } = useSucursales();

  // Determinar filtro inicial de sucursal basado en contexto
  const getInitialSucursalFilter = useCallback(() => {
    if (canViewAllSucursales && isAllSucursalesSelected) {
      return "all";
    }
    if (sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      return String(sucursalActiva.id);
    }
    return "all";
  }, [canViewAllSucursales, isAllSucursalesSelected, sucursalActiva]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedCotizacion, setSelectedCotizacion] = useState<CotizacionListItem | null>(null);
  const [selectedCotizacionId, setSelectedCotizacionId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  const deactivateMutation = useDeactivateCotizacion({
    onSuccess: () => {
      toast.warning(
        t("ordenes.cotizaciones.toast.deactivated", { numero: selectedCotizacion?.numero })
      );
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const activateMutation = useActivateCotizacion({
    onSuccess: () => {
      toast.success(
        t("ordenes.cotizaciones.toast.activated", { numero: selectedCotizacion?.numero })
      );
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const deleteMutation = useDeleteCotizacion({
    onSuccess: () => {
      toast.error(t("ordenes.cotizaciones.toast.deleted", { numero: selectedCotizacion?.numero }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const handleViewDetail = useCallback((cotizacion: CotizacionListItem) => {
    setSelectedCotizacionId(cotizacion.id);
    setIsDetailOpen(true);
  }, []);

  const handleToggleStatus = useCallback((cotizacion: CotizacionListItem) => {
    setSelectedCotizacion(cotizacion);
    setConfirmAction(cotizacion.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((cotizacion: CotizacionListItem) => {
    setSelectedCotizacion(cotizacion);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleConfirmAction = () => {
    if (!selectedCotizacion || !confirmAction) return;
    if (confirmAction === "activate") activateMutation.mutate(selectedCotizacion.id);
    else if (confirmAction === "deactivate") deactivateMutation.mutate(selectedCotizacion.id);
    else if (confirmAction === "delete") deleteMutation.mutate(selectedCotizacion.id);
    setIsConfirmOpen(false);
    setSelectedCotizacion(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    toast.success(t("ordenes.cotizaciones.toast.created"));
    refetch();
  };

  const columns: ColumnDef<CotizacionListItem>[] = useMemo(
    () => [
      {
        accessorKey: "numero",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.cotizaciones.columns.number")} />
        ),
        cell: ({ row }) => (
          <button
            className="font-mono text-sm font-medium text-primary hover:underline"
            onClick={() => handleViewDetail(row.original)}
          >
            {row.original.numero}
          </button>
        ),
      },
      {
        accessorKey: "paciente_nombre",
        header: t("ordenes.cotizaciones.columns.patient"),
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.paciente_nombre || t("common.notSpecified")}
          </span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "sucursal_nombre",
        header: t("ordenes.cotizaciones.columns.branch"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.sucursal_nombre}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "estado",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.cotizaciones.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={getEstadoBadgeVariant(row.original.estado)}>
            {row.original.estado_display}
          </Badge>
        ),
      },
      {
        accessorKey: "fecha_vigencia",
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t("ordenes.cotizaciones.columns.validUntil")}
          />
        ),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.fecha_vigencia).toLocaleDateString()}
          </span>
        ),
      },
      {
        accessorKey: "total_examenes",
        header: t("ordenes.cotizaciones.columns.exams"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.total_examenes}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "total",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.cotizaciones.columns.total")} />
        ),
        cell: ({ row }) => (
          <span className="text-sm font-medium">L {Number(row.original.total).toFixed(2)}</span>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const cot = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewDetail(cot)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t("common.view")}
                </DropdownMenuItem>
                {cot.estado === "VIGENTE" && cot.is_active && (
                  <DropdownMenuItem onClick={() => handleViewDetail(cot)}>
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    {t("ordenes.cotizaciones.convertToOrder")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(cot)}>
                  {cot.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!cot.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(cot)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [t, handleViewDetail, handleToggleStatus, handleDelete]
  );

  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getCotizaciones,
    queryKey: "cotizaciones",
    storageKey: "cotizaciones_page_size",
    defaultPageSize: 20,
    filters: [
      { key: "is_active", defaultValue: "all", urlParam: "cot_status" },
      { key: "estado", defaultValue: "all", urlParam: "cot_estado" },
      { key: "sucursal", defaultValue: "all", urlParam: "cot_sucursal" },
    ],
    initialFilters: {
      sucursal: getInitialSucursalFilter(),
    },
  });

  // Sincronizar filtro de sucursal cuando cambia el contexto
  useEffect(() => {
    if (!isAllSucursalesSelected && sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      const sucursalId = String(sucursalActiva.id);
      if (filters.sucursal !== sucursalId) {
        setFilter("sucursal", sucursalId);
      }
    }
  }, [sucursalActiva, isAllSucursalesSelected, filters.sucursal, setFilter]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-lg font-semibold">{t("ordenes.cotizaciones.title")}</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button
            size={isMobile ? "sm" : "default"}
            onClick={() => setIsFormOpen(true)}
            disabled={isAllSucursalesSelected}
            title={
              isAllSucursalesSelected ? t("ordenes.cotizaciones.selectSucursalToCreate") : undefined
            }
          >
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("ordenes.cotizaciones.newQuote")}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("ordenes.cotizaciones.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Filtro de sucursal */}
              {isAllSucursalesSelected ? (
                <Select value={filters.sucursal} onValueChange={(v) => setFilter("sucursal", v)}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder={t("ordenes.cotizaciones.columns.branch")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    {sucursales.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex items-center h-10 px-3 border rounded-md bg-muted/50 text-sm">
                  <span className="text-muted-foreground mr-2">
                    {t("ordenes.cotizaciones.columns.branch")}:
                  </span>
                  <span className="font-medium truncate">{sucursalActiva?.nombre || "-"}</span>
                </div>
              )}

              <Select value={filters.estado} onValueChange={(v) => setFilter("estado", v)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("ordenes.cotizaciones.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("ordenes.cotizaciones.allStatus")}</SelectItem>
                  <SelectItem value="VIGENTE">
                    {t("ordenes.cotizaciones.status.vigente")}
                  </SelectItem>
                  <SelectItem value="CONVERTIDA">
                    {t("ordenes.cotizaciones.status.convertida")}
                  </SelectItem>
                  <SelectItem value="VENCIDA">
                    {t("ordenes.cotizaciones.status.vencida")}
                  </SelectItem>
                  <SelectItem value="CANCELADA">
                    {t("ordenes.cotizaciones.status.cancelada")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.is_active} onValueChange={(v) => setFilter("is_active", v)}>
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("ordenes.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>{t("ordenes.showingResults", { count: totalCount })}</CardDescription>
            {!isMobile && (
              <DataTableViewOptions
                table={table}
                columnLabels={{
                  numero: t("ordenes.cotizaciones.columns.number"),
                  estado: t("ordenes.cotizaciones.columns.status"),
                  fecha_vigencia: t("ordenes.cotizaciones.columns.validUntil"),
                  total: t("ordenes.cotizaciones.columns.total"),
                  sucursal_nombre: t("ordenes.cotizaciones.columns.branch"),
                }}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("ordenes.cotizaciones.noQuotes")}
            hideOnTablet={["total_examenes", "fecha_vigencia"]}
            renderMobileCard={(cot) => (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <button
                    className="font-mono text-sm font-medium text-primary hover:underline"
                    onClick={() => handleViewDetail(cot)}
                  >
                    {cot.numero}
                  </button>
                  <Badge variant={getEstadoBadgeVariant(cot.estado)}>{cot.estado_display}</Badge>
                </div>
                <p className="font-medium">{cot.paciente_nombre || t("common.notSpecified")}</p>
                {cot.sucursal_nombre && (
                  <span className="text-xs text-muted-foreground">{cot.sucursal_nombre}</span>
                )}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{new Date(cot.fecha_vigencia).toLocaleDateString()}</span>
                  <span className="font-medium text-foreground">
                    L {Number(cot.total).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-muted-foreground">
                    {cot.total_examenes} {t("ordenes.cotizaciones.columns.exams").toLowerCase()}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewDetail(cot)}>
                        <Eye className="mr-2 h-4 w-4" />
                        {t("common.view")}
                      </DropdownMenuItem>
                      {cot.estado === "VIGENTE" && cot.is_active && (
                        <DropdownMenuItem onClick={() => handleViewDetail(cot)}>
                          <ArrowRightLeft className="mr-2 h-4 w-4" />
                          {t("ordenes.cotizaciones.convertToOrder")}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleToggleStatus(cot)}>
                        {cot.is_active ? (
                          <>
                            <XCircle className="mr-2 h-4 w-4" />
                            {t("common.deactivate")}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {t("common.activate")}
                          </>
                        )}
                      </DropdownMenuItem>
                      {!cot.is_active && (
                        <DropdownMenuItem
                          onClick={() => handleDelete(cot)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t("common.delete")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      <CotizacionFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSuccess={handleFormSuccess}
      />

      {selectedCotizacionId && (
        <CotizacionDetailSheet
          open={isDetailOpen}
          onOpenChange={setIsDetailOpen}
          cotizacionId={selectedCotizacionId}
          onConverted={() => {
            refetch();
            setIsDetailOpen(false);
          }}
        />
      )}

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("ordenes.cotizaciones.confirmActivate")
            : confirmAction === "delete"
              ? t("ordenes.cotizaciones.confirmDelete")
              : t("ordenes.cotizaciones.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("ordenes.cotizaciones.confirmActivateDesc", { numero: selectedCotizacion?.numero })
            : confirmAction === "delete"
              ? t("ordenes.cotizaciones.confirmDeleteDesc", { numero: selectedCotizacion?.numero })
              : t("ordenes.cotizaciones.confirmDeactivateDesc", {
                  numero: selectedCotizacion?.numero,
                })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
